import { PropertyValues } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { DonutChartWidgetPayload, DonutChartWidgetSliceConfig } from '@nirvana/shared/dtos/types/widgets/common';
import { translateFn } from '../../../../utils/translator.helper';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as DevicesAppColumnKeys } from '../../../devicesApp/types';
import { DEVICES_TYPE, State } from '../../../common/constants';
import { GzDonutChartWidgetWebComponent } from '../../../../ui/gzDonutChartWidget';
import { WEB_COMPONENT_GZ_RM_SCANNED_RESOURCES_WIDGET } from '../../../../webcomponents.constants';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-scanned-resources-widget
 */
@customElement(WEB_COMPONENT_GZ_RM_SCANNED_RESOURCES_WIDGET)
export class GzRmScannedResourcesWidgetWebComponent extends GzDonutChartWidgetWebComponent {
  @property({ type: Object, attribute: true })
  payload: DonutChartWidgetPayload & { companyId?: string };

  private hoverColor: string;

  constructor() {
    super();
  }

  firstUpdated() {
    super.firstUpdated();
    this.hoverColor = getComputedStyle(document.body).getPropertyValue('--blue-layout-400-primary');
  }

  async updated(changedProperties: PropertyValues) {
    await super.updated(changedProperties);
    this.style.setProperty('--hover-color', this.hoverColor);
    this.style.setProperty('--top-padding', '0');
  }

  willUpdate(properties: PropertyValues) {
    super.willUpdate(properties);

    if (properties.has('payload')) {
      if (this.payload.donutChart?.config) {
        this.payload.donutChart.config.label = translateFn('scanned resources');
        this.payload.donutChart.config.slices = this.payload.donutChart.config.slices.map(slice => {
          const id = parseInt(slice.id, 10);
          // See GzRmScannedResources.getWidgetData for more info, id is a union of EndpointType & OperatingSystem
          const label =
            id === DEVICES_TYPE.ENDPOINT
              ? translateFn('Endpoints')
              : BaseWebComponentTranslator.getInstance().getOperatingSystem(id);
          return {
            ...slice,
            label,
          };
        });
      }
    }
  }

  public handleTitleClick(): void {
    this.viewAll();
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.companyId ?? '' },
      section: 'resources',
      filters: [
        {
          [DevicesAppColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [DevicesAppColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      newTab: true,
    });
  }

  protected handleChartLegendItemClick(sliceConfig: DonutChartWidgetSliceConfig) {
    const id = parseInt(sliceConfig.id, 10);

    // See GzRmScannedResources.getWidgetData for more info, id is a union of EndpointType & OperatingSystem
    const typeFilter =
      id === DEVICES_TYPE.ENDPOINT
        ? {
            [DevicesAppColumnKeys.DEVICE_TYPE]: { in: [DEVICES_TYPE.ENDPOINT, DEVICES_TYPE.SERVER] },
          }
        : { [DevicesAppColumnKeys.OS]: { in: [id] } };

    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.companyId ?? '' },
      section: 'resources',
      filters: [
        typeFilter,
        {
          [DevicesAppColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [DevicesAppColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      newTab: true,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-scanned-resources-widget': GzRmScannedResourcesWidgetWebComponent;
  }
}
